import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  HelpCircle, 
  ArrowDown,
  Printer,
  Sparkles,
  Loader2,
  CheckCircle2
} from "lucide-react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";

export const FiveWhysTemplate = ({ onAIAssist }) => {
  const [problem, setProblem] = useState("");
  const [whys, setWhys] = useState([
    { question: "Why did this happen?", answer: "" },
    { question: "Why?", answer: "" },
    { question: "Why?", answer: "" },
    { question: "Why?", answer: "" },
    { question: "Why?", answer: "" },
  ]);
  const [rootCause, setRootCause] = useState("");
  const [countermeasure, setCountermeasure] = useState("");
  const [aiLoading, setAiLoading] = useState(false);

  const updateWhy = (index, value) => {
    setWhys(prev => prev.map((w, i) => i === index ? { ...w, answer: value } : w));
  };

  const getAISuggestions = async () => {
    if (!problem.trim()) {
      toast.error("Please enter the problem statement first");
      return;
    }

    setAiLoading(true);
    try {
      const whyAnswers = whys.map(w => w.answer || "");

      const response = await axios.post(`${API}/ai/5whys`, {
        problem: problem.trim(),
        whys: whyAnswers
      }, { withCredentials: true });

      if (onAIAssist) {
        onAIAssist(response.data.draft);
      }
      toast.success("AI analysis complete - check the chat");
    } catch (error) {
      toast.error("Failed to get AI suggestions");
    } finally {
      setAiLoading(false);
    }
  };

  const handlePrint = () => window.print();

  return (
    <Card className="bento-card print:shadow-none print:border-gray-300">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <HelpCircle className="w-5 h-5 text-blue-400" />
            5 Whys Analysis
          </CardTitle>
          <div className="flex gap-2 no-print">
            <Button variant="outline" size="sm" onClick={handlePrint}>
              <Printer className="w-4 h-4 mr-1" /> Print
            </Button>
            <Button size="sm" onClick={getAISuggestions} disabled={aiLoading}>
              {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
              AI Assist
            </Button>
          </div>
        </div>
        <p className="text-sm text-muted-foreground">
          Keep asking &quot;why&quot; to drill down to the root cause
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Problem Statement */}
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <Label className="text-red-400 font-medium">Problem Statement</Label>
          <Textarea
            value={problem}
            onChange={(e) => setProblem(e.target.value)}
            placeholder="Describe the problem or incident clearly..."
            className="mt-2 bg-transparent border-red-500/30"
            rows={2}
            data-testid="5whys-problem"
          />
        </div>

        {/* The 5 Whys Chain */}
        <div className="space-y-3">
          {whys.map((why, index) => (
            <div key={index} className="relative">
              {index > 0 && (
                <div className="absolute -top-3 left-6 text-muted-foreground">
                  <ArrowDown className="w-4 h-4" />
                </div>
              )}
              <div className={`p-3 rounded-lg border ${
                why.answer 
                  ? "bg-blue-500/10 border-blue-500/30" 
                  : "bg-zinc-800/50 border-zinc-700"
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    why.answer ? "bg-blue-500 text-white" : "bg-zinc-700 text-zinc-400"
                  }`}>
                    {index + 1}
                  </div>
                  <Label className="font-medium">{why.question}</Label>
                </div>
                <Textarea
                  value={why.answer}
                  onChange={(e) => updateWhy(index, e.target.value)}
                  placeholder={index === 0 
                    ? "Why did the problem occur?" 
                    : `Why did "${whys[index-1].answer.slice(0, 30) || '...'}" happen?`
                  }
                  className="bg-transparent"
                  rows={2}
                  data-testid={`5whys-why-${index}`}
                />
              </div>
            </div>
          ))}
        </div>

        {/* Root Cause */}
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
          <Label className="text-emerald-400 font-medium flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" />
            Root Cause Identified
          </Label>
          <Textarea
            value={rootCause}
            onChange={(e) => setRootCause(e.target.value)}
            placeholder="The true root cause is..."
            className="mt-2 bg-transparent border-emerald-500/30"
            rows={2}
            data-testid="5whys-root-cause"
          />
        </div>

        {/* Countermeasure */}
        <div className="p-4 bg-violet-500/10 border border-violet-500/30 rounded-lg">
          <Label className="text-violet-400 font-medium">Countermeasure / Action</Label>
          <Textarea
            value={countermeasure}
            onChange={(e) => setCountermeasure(e.target.value)}
            placeholder="To prevent recurrence, we will..."
            className="mt-2 bg-transparent border-violet-500/30"
            rows={2}
            data-testid="5whys-countermeasure"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default FiveWhysTemplate;
